import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, ExternalLink, FileText, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, Panel, inputCls, itDate } from "@/components/kit";
import { ShareButton } from "@/components/ShareDialog";
import { api, errMsg, fmtMoney, openPdf } from "@/lib/api";

const STATE = { pagata: "bg-[#DCFCE7] text-[#166534]", scaduta: "bg-[#FEE2E2] text-[#991B1B]", parziale: "bg-[#FEF3C7] text-[#92400E]", "da pagare": "bg-[#EAF5F7] text-[#0B6E8F]" };
const METODI = ["Bonifico", "POS", "Contanti", "Assegno", "Altro"];
const todayIso = () => new Date().toISOString().slice(0, 10);

function PortalBox({ c }) {
  const [url, setUrl] = useState("");
  const open = async () => {
    try {
      const r = await api.post(`/contracts/${c.id}/portal`);
      setUrl(window.location.origin + r.data.path);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const d = c.data || {};
  return (
    <Panel title="Area riservata del cliente" testId="portal-box">
      <p className="text-sm text-[#47606B]">Il cliente vede contratto, pagamenti, menù e conto alla rovescia, paga le rate online con carta e compila la lista invitati con le allergie.</p>
      {!url ? <Button variant="outline" className="rounded-full" onClick={open} data-testid="portal-open-button">Genera il link dell'area cliente</Button> : (
        <div className="space-y-3">
          <div className="text-xs break-all rounded-md bg-[#F5FAFB] border border-[#D4E6EC] p-2" data-testid="portal-url">{url}</div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copiato"); }} data-testid="portal-copy"><Copy size={13} className="mr-1" /> Copia</Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => window.open(url, "_blank")} data-testid="portal-preview"><ExternalLink size={13} className="mr-1" /> Apri</Button>
            <ShareButton size="sm" testId="portal-whatsapp" label="WhatsApp" link={url} linkLabel="🔐 La sua area riservata:" phone={d.cliente_tel}
              body={`🌟 Gentile *${d.cliente_nome || "Cliente"}*,\n\n✨ abbiamo creato la sua *area riservata* per il suo evento! Qui potrà:\n📄 rivedere il contratto\n💳 controllare e pagare le rate online\n👥 inserire la lista degli invitati con eventuali allergie\n⏳ seguire il conto alla rovescia per il grande giorno!\n\nUn cordiale saluto 🌊`} />
          </div>
        </div>
      )}
    </Panel>
  );
}

export function PaymentsPanel({ c }) {
  const [p, setP] = useState(null);
  const [f, setF] = useState({ rata: "rata1", importo: "", data: todayIso(), metodo: "Bonifico", note: "" });
  const load = () => api.get(`/contracts/${c.id}/payments`).then((r) => {
    setP(r.data);
    const next = r.data.plan.rate.find((x) => x.residuo > 0);
    if (next) setF((x) => ({ ...x, rata: next.key, importo: next.residuo }));
  }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.id]);
  const add = async () => {
    try {
      await api.post(`/contracts/${c.id}/payments`, { ...f, importo: Number(f.importo) });
      toast.success("Pagamento registrato");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare questo pagamento?")) return;
    try {
      await api.delete(`/contracts/${c.id}/payments/${id}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!p) return <div className="text-sm text-[#7B929C]">Caricamento…</div>;
  const d = c.data || {};
  return (
    <div className="space-y-6">
      <Panel title="Piano dei pagamenti" testId="payments-plan">
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div><div className="text-xs text-[#7B929C]">Totale</div><b data-testid="plan-total">{fmtMoney(p.plan.totale)}</b></div>
          <div><div className="text-xs text-[#7B929C]">Versato</div><b className="text-[#166534]" data-testid="plan-paid">{fmtMoney(p.plan.pagato)}</b></div>
          <div><div className="text-xs text-[#7B929C]">Residuo</div><b data-testid="plan-residual">{fmtMoney(p.plan.residuo)}</b></div>
        </div>
        {p.plan.rate.length === 0 && <div className="text-sm text-[#92400E]">Compila prezzo e rate nella «Scheda» (pulsante «Calcola prezzo di riferimento e rate»).</div>}
        {p.plan.rate.map((r) => (
          <div key={r.key} className="flex flex-wrap items-center justify-between gap-2 border-t border-[#EAF5F7] pt-3 text-sm" data-testid={`plan-row-${r.key}`}>
            <div><b>{r.label}</b><div className="text-xs text-[#7B929C]">{r.scadenza ? `scadenza ${itDate(r.scadenza)}` : "scadenza da definire"}</div></div>
            <div className="text-right">{fmtMoney(r.importo)}<div className="text-xs text-[#7B929C]">versato {fmtMoney(r.pagato)}</div></div>
            <span className={`text-[10px] uppercase px-2 py-1 rounded-full ${STATE[r.stato]}`} data-testid={`plan-state-${r.key}`}>{r.stato}</span>
          </div>
        ))}
      </Panel>
      <Panel title="Registra un pagamento" testId="payment-form">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Field label="Rata"><select className={inputCls} value={f.rata} onChange={(e) => setF({ ...f, rata: e.target.value })} data-testid="payment-rata">{p.plan.rate.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}<option value="altro">Altro / extra</option></select></Field>
          <Field label="Importo (€)"><input type="number" className={inputCls} value={f.importo} onChange={(e) => setF({ ...f, importo: e.target.value })} data-testid="payment-amount" /></Field>
          <Field label="Data"><input type="date" className={inputCls} value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} data-testid="payment-date" /></Field>
          <Field label="Modalità"><select className={inputCls} value={f.metodo} onChange={(e) => setF({ ...f, metodo: e.target.value })} data-testid="payment-method">{METODI.map((m) => <option key={m}>{m}</option>)}</select></Field>
          <Field label="Note (es. CRO bonifico)" wide><input className={inputCls} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="payment-note" /></Field>
        </div>
        <Button className="rounded-full" onClick={add} disabled={!Number(f.importo) || c.status === "annullato"} data-testid="payment-save">Registra e crea ricevuta</Button>
      </Panel>
      <Panel title="Pagamenti ricevuti" testId="payments-list">
        {p.payments.length === 0 && <div className="text-sm text-[#7B929C]">Nessun pagamento registrato.</div>}
        {p.payments.map((x) => (
          <div key={x.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-[#EAF5F7] pb-3 text-sm" data-testid={`payment-row-${x.id}`}>
            <div><b>{fmtMoney(x.importo)}</b> • {itDate(x.data)} • {x.metodo}<div className="text-xs text-[#7B929C]">Ricevuta {x.numero} {x.note && `• ${x.note}`}</div></div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => openPdf(`/contracts/${c.id}/payments/${x.id}/receipt`)} data-testid={`payment-receipt-${x.id}`}><FileText size={13} className="mr-1" /> Ricevuta</Button>
              <ShareButton size="sm" testId={`payment-share-${x.id}`} label="WhatsApp" doc={{ kind: "receipt", id: c.id, scope: x.id }} phone={d.cliente_tel} fileName={`Ricevuta-${x.numero}.pdf`}
                body={`🌟 Gentile *${d.cliente_nome || "Cliente"}*,\n\n✅ confermiamo di aver ricevuto il suo pagamento di *${fmtMoney(x.importo)}* del ${itDate(x.data)}. Grazie! 🙏\n🧾 In allegato la ricevuta n. ${x.numero}.\n\nUn cordiale saluto 🌊`} />
              {!x.session_id && <Button size="sm" variant="ghost" className="rounded-full text-[#991B1B]" onClick={() => del(x.id)} data-testid={`payment-delete-${x.id}`}><Trash2 size={13} /></Button>}
            </div>
          </div>
        ))}
      </Panel>
      <PortalBox c={c} />
    </div>
  );
}
